import { useCallback, useEffect, useState } from 'react';
import { HeaderNav } from '../components/HeaderNav';
import { PublishDecisionSet } from '../components/PublishDecisionSet';
import { Link } from 'react-router-dom';
import { getAccessToken } from '../services/api';

/**
 * The visualization gallery (KB 172).
 *
 * The listing used to be a const array in this file, which meant shipping a page and listing it
 * were two edits in two repos' worth of context, and the second one got forgotten. It now comes
 * from two places, neither of them here:
 *
 *   /viz/manifest.json     built by viz-src/build.mjs from gallery.json + the inlined pages.
 *                          Static, same image as the pages, so it cannot disagree with them.
 *   /api/decision-sets     decision sets published through the API since the image was built.
 *                          Open reads, so this page needs no token for them.
 *
 * A published set already named by a manifest entry (matched on `set`) does not get a second
 * card — it annotates the one that is there with the live version, so you can see at a glance
 * whether the stored copy has moved past the page baked into this build.
 */

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface VizDemo {
  id: string;
  title: string;
  description: string;
  src: string;
  tech: string;
  icon?: string;
  tags: string[];
  /** Set slug, when this card renders a decision set. */
  set?: string;
  rows?: number;
  options?: number;
  /** Live version in the API, if it has one. Absent means "not published to storage". */
  publishedVersion?: number;
  status?: string;
  decided?: number;
}

interface PublishedSet {
  id: string;
  title: string;
  subtitle?: string;
  status: string;
  rows: number;
  options: number;
  decided: number;
  version: number;
  href: string;
}

function describeSet(s: PublishedSet): string {
  const decided = s.decided
    ? `${s.decided} of ${s.rows} row${s.rows === 1 ? '' : 's'} decided`
    : 'no choices made yet';
  return `${s.subtitle ? s.subtitle + ' ' : ''}${s.rows} rows across ${s.options} options — ${decided}.`;
}

async function loadGallery(): Promise<VizDemo[]> {
  const manifest = await fetch('/viz/manifest.json').then((r) => {
    if (!r.ok) throw new Error(`manifest ${r.status}`);
    return r.json();
  });
  const entries: VizDemo[] = manifest.entries ?? [];

  // A published-set lookup failing is not a broken gallery — the static pages are the gallery.
  let published: PublishedSet[] = [];
  try {
    const res = await fetch(`${BASE_URL}/decision-sets`);
    if (res.ok) published = (await res.json()).sets ?? [];
  } catch {
    published = [];
  }

  const bySet = new Map(published.map((s) => [s.id, s]));
  const annotated = entries.map((e) => {
    const live = e.set ? bySet.get(e.set) : undefined;
    if (live) bySet.delete(e.set!);
    return live
      ? { ...e, publishedVersion: live.version, status: live.status, decided: live.decided }
      : e;
  });

  const extra: VizDemo[] = [...bySet.values()].map((s) => ({
    id: `set-${s.id}`,
    title: s.title,
    description: describeSet(s),
    src: s.href,
    tech: 'Decision Set',
    icon: '🧾',
    tags: ['decision-set', s.status],
    set: s.id,
    rows: s.rows,
    options: s.options,
    publishedVersion: s.version,
    status: s.status,
    decided: s.decided,
  }));

  return [...annotated, ...extra];
}

export function VizPage() {
  // Token hand-off for self-contained /viz pages that need the API (e.g. /viz/observability.html).
  // The page posts { type: 'barnyard:token:request' }; we answer with the signed-in user's token.
  // Same-origin both ways: the iframe is same-origin (sandbox allow-same-origin), so a message from
  // any other origin is ignored and the reply is addressed to our own origin only.
  useEffect(() => {
    const onMessage = async (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (!e.data || e.data.type !== 'barnyard:token:request') return;
      const token = await getAccessToken();
      (e.source as Window | null)?.postMessage({ type: 'barnyard:token', token, apiBase: '/api' }, window.location.origin);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const [activeDemo, setActiveDemo] = useState<VizDemo | null>(null);
  const [demos, setDemos] = useState<VizDemo[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    loadGallery()
      .then(setDemos)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    let live = true;
    loadGallery()
      .then((d) => live && setDemos(d))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, []);

  return (
    <div className="h-screen bg-tower-bg text-tower-text flex flex-col">
      <header className="h-14 bg-tower-surface border-b border-tower-border flex items-center px-6 gap-4 shrink-0">
        <Link to="/" className="text-lg font-mono font-bold text-tower-accent tracking-wider hover:text-tower-text transition-colors">
          MAESTRO
        </Link>
        <div className="h-4 w-px bg-tower-border" />
        <span className="text-sm text-success font-semibold">Visualizations</span>
        <div className="flex-1" />
        <HeaderNav />
      </header>

      {activeDemo ? (
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-2 bg-tower-surface border-b border-tower-border shrink-0">
            <button
              onClick={() => setActiveDemo(null)}
              className="text-xs text-tower-muted hover:text-tower-accent transition-colors"
            >
              &larr; Back to Gallery
            </button>
            <div className="h-4 w-px bg-tower-border" />
            <span className="text-sm font-semibold text-tower-text">{activeDemo.title}</span>
            <span className="text-xs text-tower-muted bg-tower-border/50 px-2 py-0.5 rounded">{activeDemo.tech}</span>
            <div className="flex-1" />
            <a
              href={activeDemo.src}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-tower-muted hover:text-tower-accent transition-colors"
            >
              Open fullscreen &rarr;
            </a>
          </div>
          <iframe
            src={activeDemo.src}
            className="flex-1 w-full border-0"
            title={activeDemo.title}
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      ) : (
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold text-tower-accent mb-2">Visualization Lab</h1>
            <p className="text-sm text-tower-muted mb-6 max-w-2xl">
              Interactive data visualizations for dairy nutrition formulation analysis.
              These demos show how nutrient-ingredient-product relationships can be explored
              visually — a preview of agentic visualization capabilities coming to Barnyard.
            </p>

            <PublishDecisionSet onPublished={refresh} />

            {error && (
              <div className="p-4 mb-4 bg-tower-surface border border-tower-border rounded-lg text-sm text-tower-muted">
                Could not load the gallery listing ({error}). The pages themselves are still
                served — open <span className="font-mono text-tower-text">/viz/&lt;page&gt;.html</span> directly.
              </div>
            )}

            {!demos && !error && <div className="text-sm text-tower-muted">Loading gallery…</div>}

            <div className="grid gap-4">
              {(demos ?? []).map((demo) => (
                <button
                  key={demo.id}
                  onClick={() => setActiveDemo(demo)}
                  className="text-left p-5 bg-tower-surface border border-tower-border rounded-lg hover:border-tower-accent/50 transition-all group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-base font-semibold text-tower-text group-hover:text-tower-accent transition-colors">
                          {demo.title}
                        </h2>
                        <span className="text-[10px] text-tower-muted bg-tower-border/50 px-1.5 py-0.5 rounded font-mono">
                          {demo.tech}
                        </span>
                        {demo.publishedVersion !== undefined && (
                          <span className="text-[10px] text-success border border-success rounded px-1.5 py-0.5 font-mono">
                            published v{demo.publishedVersion}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-tower-muted leading-relaxed">{demo.description}</p>
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {demo.tags.map((tag) => (
                          <span key={tag} className="text-[10px] text-tower-muted border border-tower-border rounded px-1.5 py-0.5">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="w-24 h-16 bg-tower-border/30 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-tower-accent/10 transition-colors">
                      <span className="text-2xl opacity-50 group-hover:opacity-80 transition-opacity">
                        {demo.icon ?? '📊'}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-8 p-4 bg-tower-surface border border-tower-border rounded-lg">
              <h3 className="text-sm font-semibold text-tower-accent mb-2">Roadmap: Agentic Visualizations</h3>
              <p className="text-xs text-tower-muted leading-relaxed">
                These static demos are the foundation for a larger capability: agents that generate,
                update, and interpret visualizations dynamically. Future phases will connect Studio's
                SQL artifacts to live visualization widgets — edit a query, see the chart update in
                real-time. Agents will use visualization as a reasoning tool, not just a presentation layer.
              </p>
            </div>
          </div>
        </main>
      )}
    </div>
  );
}
